import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Doughnut, Line } from 'react-chartjs-2';
import { Link, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import '../css/ControlCenter.css';  // 새로운 CSS 파일을 import
import NavigationBar from '../components/NavigationBar'; // NavigationBar 컴포넌트 import
import SendSmsDialog from '../components/SendSmsDialog';
import EducationalVideoDialog from '../components/EducationalVideoDialog';
import { Chart as ChartJS } from 'chart.js/auto';
import { API_BASE_URL } from '../config/config';
import {
    buildScopeParams,
    fetchPatientRoster,
    fetchPatientMeasurements,
    fetchPatientWorsening
} from '../utils/patientsApi';
import 'chartjs-adapter-date-fns';
import { ko } from 'date-fns/locale';
import { riskStatusKey } from '../components/RiskBadge';
import { TrendingUp, ChevronDown, ChevronUp } from 'lucide-react';

const BP_SYSTOLIC_ORDER = [
    "위험 (고혈압)",
    "경고 (고혈압 주의)",
    "정상",
    "위험 (저혈압)",
    "기록 없음"
];

const BP_DIASTOLIC_ORDER = [
    "위험 (고혈압)",
    "경고 (고혈압 주의)",
    "정상",
    "위험 (저혈압)",
    "기록 없음"
];

const BMI_ORDER = [
    "위험 (비만)",
    "경고 (과체중 주의)",
    "정상",
    "위험 (저체중)",
    "기록 없음"
];

const getRiskLevel = (value, type) => {
    if (value === null || value === undefined || value === 0 || value === '') {
        return { level: "기록 없음", color: "#AAAAAA" };
    }
    
    switch (type) {
        case 'BLOOD_GLUCOSE':
            if (value < 70) return { level: "위험 (저혈당)", color: "#000080" };
            if (value <= 100) return { level: "정상", color: "#2ECC40" };
            if (value <= 125) return { level: "경고 (고혈당 주의)", color: "#FFDC00" };
            if (value > 125) return { level: "위험 (고혈당)", color: "#FF4136" };
            return { level: "기록 없음", color: "#AAAAAA" };
        case 'BLOOD_GLUCOSE_MEAL':
            if (value < 90) return { level: "위험 (저혈당)", color: "#000080" };
            if (value <= 140) return { level: "정상", color: "#2ECC40" };
            if (value <= 200) return { level: "경고 (고혈당 주의)", color: "#FFDC00" };
            if (value > 200) return { level: "위험 (고혈당)", color: "#FF4136" };
            return { level: "기록 없음", color: "#AAAAAA" };
        case 'BP_SYSTOLIC':
            if (value < 90) return { level: "위험 (저혈압)", color: "#000080" };
            if (value <= 120) return { level: "정상", color: "#2ECC40" };
            if (value <= 140) return { level: "경고 (고혈압 주의)", color: "#FFDC00" };
            if (value > 140) return { level: "위험 (고혈압)", color: "#FF4136" };
            return { level: "기록 없음", color: "#AAAAAA" };
        case 'BP_DIASTOLIC':
            if (value < 60) return { level: "위험 (저혈압)", color: "#000080" };
            if (value <= 80) return { level: "정상", color: "#2ECC40" };
            if (value <= 90) return { level: "경고 (고혈압 주의)", color: "#FFDC00" };
            if (value > 90) return { level: "위험 (고혈압)", color: "#FF4136" };
            return { level: "기록 없음", color: "#AAAAAA" };
        case 'BMI':
            if (value < 18.5) return { level: "위험 (저체중)", color: "#000080" };
            if (value <= 23.0) return { level: "정상", color: "#2ECC40" };
            if (value <= 25.0) return { level: "경고 (과체중 주의)", color: "#FFDC00" };
            if (value > 25.0) return { level: "위험 (비만)", color: "#FF4136" };
            return { level: "기록 없음", color: "#AAAAAA" };
        default:
            return { level: "기록 없음", color: "#AAAAAA" };
    }
};

// 안전 구역 범위 정의
const safeRanges = {
    BLOOD_GLUCOSE: {
        upper: { y: 160, color: '#FF4136' },
        lower: { y: 70, color: '#FF851B' },
        target: { min: 70, max: 160, color: 'rgba(46, 204, 64, 0.1)' }
    },
    BLOOD_GLUCOSE_MEAL: {
        upper: { y: 200, color: '#FF4136' },
        lower: { y: 90, color: '#FF851B' },
        target: { min: 90, max: 200, color: 'rgba(46, 204, 64, 0.1)' }
    },
    BP_SYSTOLIC: {
        systolic: {
            upper: { y: 140, color: '#FF4136' },
            lower: { y: 90, color: '#FF851B' },
            target: { min: 90, max: 140, color: 'rgba(46, 204, 64, 0.1)' }
        },
        diastolic: {
            upper: { y: 90, color: '#FF4136' },
            lower: { y: 60, color: '#FF851B' },
            target: { min: 60, max: 90, color: 'rgba(46, 204, 64, 0.1)' }
        }
    },
    BMI: {
        upper: { y: 25, color: '#FF4136' },
        lower: { y: 18.5, color: '#FF851B' },
        target: { min: 18.5, max: 25, color: 'rgba(46, 204, 64, 0.1)' }
    }
};

// 안전 범위 표시 플러그인
const drawSafeRanges = (chart) => {
    const { ctx, chartArea, scales } = chart;
    if (!ctx || !chartArea || !scales) return;
    const safeRanges = chart.options.safeRanges;
    if (!Array.isArray(safeRanges)) return;
    safeRanges.forEach(range => {
        if (!range || range.min == null || range.max == null) return;
        ctx.save();
        ctx.fillStyle = range.color;
        ctx.fillRect(
            chartArea.left,
            scales.y.getPixelForValue(range.max),
            chartArea.right - chartArea.left,
            scales.y.getPixelForValue(range.min) - scales.y.getPixelForValue(range.max)
        );
        ctx.beginPath();
        ctx.strokeStyle = range.lineColor;
        ctx.lineWidth = 2;
        ctx.moveTo(chartArea.left, scales.y.getPixelForValue(range.max));
        ctx.lineTo(chartArea.right, scales.y.getPixelForValue(range.max));
        ctx.moveTo(chartArea.left, scales.y.getPixelForValue(range.min));
        ctx.lineTo(chartArea.right, scales.y.getPixelForValue(range.min));
        ctx.stroke();
        ctx.restore();
    });
};

// 플러그인 등록
ChartJS.register({
    id: 'safeRangesPlugin',
    beforeDraw: (chart) => {
        if (chart.options && typeof chart.options.getSafeRanges === 'function') {
            chart.options.safeRanges = chart.options.getSafeRanges();
        }
        drawSafeRanges(chart);
    }
});

// 로스터 1행(사용자 + 지표별 최신값)을 화면들이 쓰던 "레코드 배열" 모양으로 되돌린다.
// 렌더 코드(renderGrid / calculateDistribution / handleUserClick)가 전부 이 모양을
// 전제하고 있어서, 여기서 맞춰 주면 그쪽은 손댈 필요가 없다.
const toRecords = (item) => (item.measurements || []).map((measurement) => ({
    app_user_id: item.appUserId,
    name: item.name,
    birthdate: item.birthdate,
    gender: item.gender,
    group_name: item.groupName,
    management_group: item.groupName,
    focuspatient: item.focusPatient,
    last_modified_date: item.lastModifiedDate,
    measure_type: measurement.measureType,
    current_value: Number(measurement.currentValue),
    before_value: Number(measurement.beforeValue),
    measure_date: measurement.measureDate,
    date: measurement.measureDate
}));

// 탭 필터. 예전에는 fetch 안에 있어서 탭을 바꿀 때마다 재조회가 일어났다.
// 로스터가 5개 지표를 모두 실어 오므로 이제 순수 계산이다.
const filterByTab = (recordsByUser, activeTab) => {
    const result = {};
    Object.keys(recordsByUser).forEach((userId) => {
        const sorted = [...recordsByUser[userId]].sort(
            (a, b) => new Date(b.measure_date) - new Date(a.measure_date)
        );

        let kept;
        if (activeTab === 'INTENSIVE_CARE') {
            kept = sorted;
        } else if (activeTab === 'BP_SYSTOLIC') {
            kept = sorted.filter((record) => record.measure_type === 'BP_SYSTOLIC' || record.measure_type === 'BP_DIASTOLIC');
        } else {
            kept = sorted.filter((record) => record.measure_type === activeTab);
        }

        // 해당 탭 기록이 없어도 이름은 남긴다. 그리드가 "기록 없음" 칸을 그린다.
        if (kept.length === 0 && sorted.length > 0) {
            kept = [{ name: sorted[0].name, measure_type: null }];
        }
        result[userId] = kept;
    });
    return result;
};

// yyyy-mm-dd. 로컬 시각 기준으로 자른다 —
// toISOString() 을 쓰면 UTC 로 넘어가면서 새벽 측정이 하루 앞당겨진다.
const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
};

// ControlCenter.js 의 getRiskLevel 이 실제로 만드는 라벨에서 등급을 뽑는다.
//
// ⚠️ 예전 getWorseningUsers 는 "고혈당 위험" 같은 별도의 문자열 표로 점수를 매겼는데,
//    getRiskLevel 은 "위험 (고혈당)" 형태를 만든다. 두 어휘가 한 글자도 맞지 않아
//    '정상'(3)과 '기록 없음'(1) 말고는 전부 0 으로 떨어졌고, 결과적으로 목록이
//    뒤집혀 있었다 — 호전한 사람이 올라오고 실제 악화는 빠졌다.
//    dev 실측(BLOOD_GLUCOSE): 기존 목록은 위험→정상, 경고→정상 두 명이었고
//    실제 악화(경고→위험) 두 명은 빠져 있었다.
//
// '기록 없음' 은 null 이다. 측정을 시작한 것은 악화가 아니다.
const severityRank = (level) => {
    if (typeof level !== 'string') return null;
    if (level.startsWith('위험')) return 2;
    if (level.startsWith('경고')) return 1;
    if (level === '정상') return 0;
    return null;
};

export default function ControlCenter() {
    // 중점관리 그룹은 한 그룹에 수십 명이 들어가기도 한다. 기본은 10명까지만 펼쳐
    // 두고 나머지는 "더 보기"로 넘긴다. 그룹마다 따로 기억한다.
    const [expandedGroups, setExpandedGroups] = useState({});
    const [data, setData] = useState({});
    const [roster, setRoster] = useState(null);
    const [worsening, setWorsening] = useState([]);
    const [isWorseningOpen, setIsWorseningOpen] = useState(false);
    // 상세 모달에서 여는 발송 다이얼로그. 모달 위에 겹쳐 뜬다.
    const [isSmsOpen, setIsSmsOpen] = useState(false);
    const [isVideoOpen, setIsVideoOpen] = useState(false);
    const [cameFromWorsening, setCameFromWorsening] = useState(false);
    const [activeTab, setActiveTab] = useState('INTENSIVE_CARE');
    const [selectedUser, setSelectedUser] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [safeRange, setSafeRange] = useState(null);
    const navigate = useNavigate();
    const [filterType, setFilterType] = useState('all');
    const [systolicRange, setSystolicRange] = useState(null);
    const [diastolicRange, setDiastolicRange] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // 토큰 검증 로직 추가
    useEffect(() => {
        const userRole = localStorage.getItem('user_role') || (JSON.parse(localStorage.getItem('adminInfo') || '{}').role);
        if (userRole === 'guardian') return; // 보호자면 검증 스킵
        const token = localStorage.getItem('token');

        if (!token) {
            alert('로그인이 필요합니다.');
            navigate('/');
            return;
        }

        // 토큰 검증
        try {
            const decodedToken = jwtDecode(token); // jwt-decode 사용법 수정
            const currentTime = Date.now() / 1000;

            if (decodedToken.exp < currentTime) {
                alert('세션이 만료되었습니다. 다시 로그인해주세요.');
                localStorage.removeItem('token');
                navigate('/');
                return;
            }
        } catch (error) {
            console.error('토큰 검증 실패:', error);
            alert('인증에 실패했습니다. 다시 로그인해주세요.');
            localStorage.removeItem('token');
            navigate('/');
            return;
        }
    }, [navigate]);

    // 필터 변경 이벤트 감지
    useEffect(() => {
        const handleFilterChange = (event) => {
            setFilterType(event.detail.type);
        };
        window.addEventListener('filterChange', handleFilterChange);
        return () => window.removeEventListener('filterChange', handleFilterChange);
    }, []);

    useEffect(() => {
        const fetchGuardianData = async () => {
            setIsLoading(true);
            // 예시: 보호자용 데이터 API 호출
            try {
                const token = localStorage.getItem('token');
                // 실제 보호자용 API 엔드포인트로 교체 필요
                const response = await axios.get(`${API_BASE_URL}/api/guardian-controlcenter-data`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.data.success) {
                    setData(response.data.data); // 보호자용 데이터 구조에 맞게 가공 필요
                } else {
                    // setError는 필요시 추가
                }
            } catch (error) {
                // setError는 필요시 추가
            } finally {
                setIsLoading(false);
            }
        };

        const userRole = localStorage.getItem('user_role') || (JSON.parse(localStorage.getItem('adminInfo') || '{}').role);
        if (userRole === 'guardian') {
            fetchGuardianData();
            return;
        }
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const token = localStorage.getItem('token');
                const params = buildScopeParams({ filterType });
                const result = await fetchPatientRoster({ token, scopeParams: params });

                if (result.truncated) {
                    console.warn('로스터가 상한에서 잘렸습니다. 분포와 중점관리 명단이 실제보다 적게 나옵니다.');
                }

                const recordsByUser = {};
                (result.items || []).forEach((item) => {
                    recordsByUser[String(item.appUserId)] = toRecords(item);
                });
                setRoster(recordsByUser);
            } catch (error) {
                if (error.response && error.response.status === 401) {
                    alert('인증이 만료되었습니다. 다시 로그인해주세요.');
                    localStorage.removeItem('token');
                    navigate('/');
                } else {
                    console.error('Error fetching data:', error.message);
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
        // activeTab 은 의존성이 아니다. 로스터가 지표 5개를 모두 실어 오므로
        // 탭 전환은 아래의 파생 effect 에서 계산만 다시 하면 된다.
    }, [navigate, filterType]);

    // 로스터 → 탭별 data. 보호자 경로는 예전처럼 setData 를 직접 하므로 건드리지 않는다.
    useEffect(() => {
        if (roster === null) return;
        setData(filterByTab(roster, activeTab));
    }, [roster, activeTab]);

    // 기록 악화 목록은 대상자별 "최신 + 직전" 두 건이 필요한데 로스터에는 최신값만 있다.
    // 중점관리 탭에는 이 표가 없다.
    useEffect(() => {
        // 탭이 바뀌면 목록의 기준 지표가 달라진다. 열린 채로 두면 이전 지표의
        // 명단이 남는다. 차트를 연 상태로 탭을 옮겼다면 돌아갈 목록도 사라진 셈이다.
        setIsWorseningOpen(false);
        setCameFromWorsening(false);

        if (activeTab === 'INTENSIVE_CARE') {
            setWorsening([]);
            return;
        }
        const userRole = localStorage.getItem('user_role') || (JSON.parse(localStorage.getItem('adminInfo') || '{}').role);
        if (userRole === 'guardian') return;

        let cancelled = false;
        (async () => {
            try {
                const token = localStorage.getItem('token');
                const result = await fetchPatientWorsening({
                    token,
                    scopeParams: buildScopeParams({ filterType }),
                    metric: activeTab
                });
                if (!cancelled) setWorsening(result.items || []);
            } catch (error) {
                if (!cancelled) {
                    console.error('기록 악화 목록 조회 실패:', error.message);
                    setWorsening([]);
                }
            }
        })();
        return () => { cancelled = true; };
    }, [activeTab, filterType]);

    // 안전범위 동적 조회 함수
    const fetchSafeRange = async (measureType) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/api/measurement-signal-config`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { measure_type: measureType }
            });
            if (response.data && response.data.success && response.data.data) {
                if (measureType === 'BP_SYSTOLIC' || measureType === 'BP_DIASTOLIC') {
                    // NORMAL, WARNING 구간 모두 추출
                    const ranges = response.data.data
                        .filter(row => row.signal_type === 'NORMAL' || row.signal_type === 'WARNING')
                        .map(row => {
                            let min = Number(row.greater_than_equal);
                            let max = row.lower_than_equal !== null && row.lower_than_equal !== undefined
                                ? Number(row.lower_than_equal)
                                : Number(row.lower_than);
                            // 하한값 보정
                            if (measureType === 'BP_SYSTOLIC' && min < 90) min = 90;
                            if (measureType === 'BP_DIASTOLIC' && min < 60) min = 60;
                            return {
                                min,
                                max,
                                text: `${row.signal_type === 'NORMAL' ? '정상' : '경고'} ${measureType === 'BP_SYSTOLIC' ? '수축기' : '이완기'} 혈압 범위 (${min}~${max})`,
                                color: row.signal_type === 'NORMAL'
                                    ? (measureType === 'BP_SYSTOLIC' ? 'rgba(144,238,144,0.2)' : 'rgba(173,255,47,0.2)')
                                    : (measureType === 'BP_SYSTOLIC' ? 'rgba(255,255,0,0.2)' : 'rgba(255,215,0,0.2)'),
                                lineColor: row.signal_type === 'NORMAL'
                                    ? (measureType === 'BP_SYSTOLIC' ? 'rgba(0,200,0,0.8)' : 'rgba(173,255,47,0.8)')
                                    : (measureType === 'BP_SYSTOLIC' ? 'rgba(255,255,0,0.8)' : 'rgba(255,215,0,0.8)'),
                                textColor: row.signal_type === 'NORMAL'
                                    ? (measureType === 'BP_SYSTOLIC' ? 'rgba(0,200,0,0.7)' : 'rgba(173,255,47,0.7)')
                                    : (measureType === 'BP_SYSTOLIC' ? 'rgba(255,255,0,0.7)' : 'rgba(255,215,0,0.7)')
                            };
                        });
                    return ranges;
                } else {
                    // 기존 로직 유지 (NORMAL만)
                    const normalConfig = response.data.data.find(row => row.signal_type === 'NORMAL');
                    if (normalConfig) {
                        const min = Number(normalConfig.greater_than_equal);
                        const max = normalConfig.lower_than_equal !== null && normalConfig.lower_than_equal !== undefined
                            ? Number(normalConfig.lower_than_equal)
                            : Number(normalConfig.lower_than);
                        return {
                            min,
                            max,
                            text: measureType === 'BLOOD_GLUCOSE' ? `정상 혈당 범위 (${min}~${max})` : `정상 BMI 범위 (${min}~${max})`
                        };
                    }
                }
            }
            return measureType === 'BP_SYSTOLIC' || measureType === 'BP_DIASTOLIC' ? [] : null;
        } catch (e) {
            return measureType === 'BP_SYSTOLIC' || measureType === 'BP_DIASTOLIC' ? [] : null;
        }
    };

    const fetchAllSafeRanges = async () => {
        const [sysRanges, diaRanges] = await Promise.all([
            fetchSafeRange('BP_SYSTOLIC'),
            fetchSafeRange('BP_DIASTOLIC')
        ]);
        setSystolicRange(sysRanges);
        setDiastolicRange(diaRanges);
        return { sysRanges, diaRanges };
    };

    // 형식은 원래도 yyyy-mm-dd 였지만 toISOString() 이라 UTC 기준이었다.
    // 새벽 측정이 하루 앞당겨져 악화 목록의 날짜와 어긋나므로 formatDate 로 모은다.
    const dateFormatter = (date) => formatDate(date);

    const handleUserClick = async (user_id) => {
        // 차트는 이력이 필요한데 로스터에는 지표별 최신값만 있다. 모달을 열 때만
        // 그 한 명의 이력을 받는다 — 예전에는 열지도 않을 모달을 위해 전원의
        // 이력을 미리 받고 있었다.
        const metrics = (activeTab === 'BP_SYSTOLIC' || activeTab === 'BP_DIASTOLIC')
            ? ['BP_SYSTOLIC', 'BP_DIASTOLIC']
            : [activeTab];

        // 사용자 식별 정보는 이미 로스터에 있다. 이력이 비어도 이름은 채워야 한다.
        const rosterRecords = (roster && roster[String(user_id)]) || [];
        const identity = rosterRecords[0] || {};

        // ⚠️ 전화번호는 로스터에 없다 — dec_text() 가 전원에 걸리는데 정작 쓰는 건
        //    모달을 연 한 명뿐이라 일부러 뺐다(repository.js 참고).
        //    교육영상 다이얼로그가 이 값을 쓰므로 여기서 한 명분만 받아 온다.
        let mobile = null;
        try {
            const token = localStorage.getItem('token');
            const detail = await axios.get(`${API_BASE_URL}/api/health-data/${user_id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            mobile = (detail.data?.data || [])[0]?.mobile || null;
        } catch (error) {
            // 전화번호를 못 받아도 차트는 그대로 보여준다. 발송 다이얼로그에서
            // 다시 시도하고, 거기서 없으면 그때 안내한다.
            console.error('대상자 연락처 조회 실패:', error.message);
        }

        let history = [];
        try {
            const token = localStorage.getItem('token');
            const result = await fetchPatientMeasurements({
                token,
                scopeParams: buildScopeParams({ filterType }),
                appUserId: user_id,
                metrics
            });
            history = result.items || [];
        } catch (error) {
            console.error('측정 이력 조회 실패:', error.message);
        }

        const rawUserData = history.map((measurement) => ({
            app_user_id: String(user_id),
            name: identity.name,
            birthdate: identity.birthdate,
            gender: identity.gender,
            group_name: identity.group_name,
            management_group: identity.management_group,
            measure_type: measurement.measureType,
            current_value: Number(measurement.currentValue),
            before_value: Number(measurement.beforeValue),
            measure_date: measurement.measureDate,
            date: measurement.measureDate
        }));

        let userData = rawUserData.map(record => ({
            ...record,
            measure_date: new Date(record.measure_date).toISOString()
        }));

        if (activeTab === 'BP_SYSTOLIC' || activeTab === 'BP_DIASTOLIC') {
            const bpData = userData.filter(record =>
                record.measure_type === 'BP_SYSTOLIC' || record.measure_type === 'BP_DIASTOLIC'
            );
            if (bpData.length > 0) {
                userData = bpData;
                console.log('Blood Pressure Data:', bpData);
            }
        }

        if (userData.length === 0) {
            // 기록이 없어도 사용자 정보는 로스터에 있다. 예전에는 여기서 전부
            // '-' 로 덮어써서 이름조차 안 나왔다.
            userData = [{
                name: identity.name || '-',
                birthdate: identity.birthdate || '-',
                gender: identity.gender || '-',
                group_name: identity.group_name || '-',
                management_group: identity.management_group || '-',
                current_value: '-',
                measure_date: new Date().toISOString(),
                measure_type: activeTab
            }];
        }

        userData = userData.sort((a, b) => new Date(a.measure_date) - new Date(b.measure_date));

        let safeRangeValue = null, diastolicRangeValue = null;
        if (activeTab === 'BP_SYSTOLIC' || activeTab === 'BP_DIASTOLIC') {
            const { sysRanges, diaRanges } = await fetchAllSafeRanges();
            safeRangeValue = sysRanges;
            diastolicRangeValue = diaRanges;
        } else if (activeTab === 'BLOOD_GLUCOSE') {
            safeRangeValue = await fetchSafeRange('BLOOD_GLUCOSE');
            setSystolicRange(safeRangeValue);
            setDiastolicRange(null);
        } else if (activeTab === 'BMI') {
            safeRangeValue = await fetchSafeRange('BMI');
            setSystolicRange(safeRangeValue);
            setDiastolicRange(null);
        }
        console.log('Safe Range Settings:', safeRangeValue, diastolicRangeValue);

        const latestRecord = userData[userData.length - 1]; // 가장 최근 기록 사용
        console.log('Selected User Information:', {
            userId: user_id,
            name: latestRecord.name || '-',
            birthdate: latestRecord.birthdate || '-',
            gender: latestRecord.gender || '-',
            management_group: latestRecord.group_name || '-',
            measureType: activeTab,
            currentValue: latestRecord.current_value || '-',
            measureDate: latestRecord.measure_date,
            riskLevel: latestRecord.current_value ? getRiskLevel(latestRecord.current_value, activeTab).level : '-'
        });

        setSelectedUser({
            userId: user_id,
            name: latestRecord.name || '-',
            birthdate: latestRecord.birthdate || '-',
            gender: latestRecord.gender || '-',
            management_group: latestRecord.group_name || '-',
            mobile,
            chartData: userData,
            safeRange: safeRangeValue,
            diastolicRange: diastolicRangeValue,
            latestValue: latestRecord.current_value || '-',
            latestRisk: latestRecord.current_value ? getRiskLevel(latestRecord.current_value, activeTab).level : '-',
            history: userData
        });
        setIsModalOpen(true);
        console.log('safeRange:', safeRangeValue, diastolicRangeValue); // 모달 열릴 때
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedUser(null);

        // 악화 목록에서 들어온 경우 목록으로 돌아간다. 여러 명을 이어서 볼 때
        // 매번 버튼을 다시 눌러야 하면 목록을 보는 의미가 없다.
        if (cameFromWorsening) {
            setCameFromWorsening(false);
            setIsWorseningOpen(true);
        }
    };

    const BLOOD_GLUCOSE_ORDER = [
        "위험 (고혈당)",
        "경고 (고혈당 주의)",
        "정상",
        "위험 (저혈당)",
        "기록 없음"
    ];

    const BLOOD_GLUCOSE_MEAL_ORDER = [
        "위험 (고혈당)",
        "경고 (고혈당 주의)",
        "정상",
        "위험 (저혈당)",
        "기록 없음"
    ];

    const renderGrid = (type) => {
        const userIds = Object.keys(data);
        const totalItems = 800; // 총 grid 수

        const riskGroups = {
            severe: ["위험 (고혈당)", "심각한 고혈당 위험", "심각한 저혈당 위험", "고도 비만"],
            risk: ["경고 (고혈당 주의)", "고혈당 위험", "저혈당 위험", "고혈압 위험", "비만", "위험 (고혈당)", "위험 (저혈당)", "위험 (고혈압)", "위험 (저혈압)", "위험 (비만)", "위험 (저체중)"],
            caution: ["고혈당 주의", "저혈당 주의", "고혈압 주의", "과체중", "경고 (고혈당 주의)", "경고 (고혈압 주의)", "경고 (과체중 주의)"],
            safe: ["정상", "안전 혈당", "정상 혈압"],
            noRecord: ["기록 없음"]
        };

        if (type === 'INTENSIVE_CARE') {
            const intensiveUsers = [];

            userIds.forEach(user_id => {
                const userData = data[user_id] || [];
                const latestMetrics = {};

                // Get the latest reading for each measure type
                userData.forEach(record => {
                    if (!latestMetrics[record.measure_type] || new Date(record.measure_date) > new Date(latestMetrics[record.measure_type].measure_date)) {
                        latestMetrics[record.measure_type] = record;
                    }
                });

                let riskCount = 0;
                const evaluatedTypes = ['BLOOD_GLUCOSE', 'BLOOD_GLUCOSE_MEAL', 'BP_SYSTOLIC', 'BP_DIASTOLIC', 'BMI'];

                evaluatedTypes.forEach(mType => {
                    const record = latestMetrics[mType];
                    if (record) {
                        const levelObj = getRiskLevel(record.current_value, mType);
                        // 위험인자 여부 판단 (위험 수준이 "위험"으로 시작하는 경우)
                        if (levelObj.level.startsWith('위험')) {
                            riskCount++;
                        }
                    }
                });

                if (riskCount >= 2) {
                    intensiveUsers.push({
                        user_id: String(user_id),
                        userName: userData[0]?.name || '알 수 없음',
                        riskCount,
                        groupName: userData[0]?.management_group || userData[0]?.group_name || '-'
                    });
                }
            });

            const users4 = intensiveUsers.filter(u => u.riskCount >= 4);
            const users3 = intensiveUsers.filter(u => u.riskCount === 3);
            const users2 = intensiveUsers.filter(u => u.riskCount === 2);

            // 한 번에 펼쳐 두는 인원. 이보다 많으면 나머지는 "더 보기" 뒤로 접는다.
            const INTENSIVE_VISIBLE = 10;

            const renderIntensiveGroup = (title, users, filterLabel, tone) => {
                const expanded = !!expandedGroups[filterLabel];
                const hidden = Math.max(0, users.length - INTENSIVE_VISIBLE);
                const shown = expanded ? users : users.slice(0, INTENSIVE_VISIBLE);

                return (
                <div className="sds-list control-center-group">
                    <button
                        className="sds-list__row sds-list__row--clickable control-center-group__head"
                        onClick={() => navigate('/Dashboard', { state: { intensiveFilter: filterLabel, groupUserIds: users.map(u => u.user_id) } })}
                        title="클릭하여 대시보드에서 상세 보기"
                    >
                        <div className="sds-list__body">
                            <b>{title} <span className={`sds-badge sds-badge--${tone}`}>{users.length}명</span></b>
                        </div>
                        <span className="sds-list__spacer" />
                        <span className="sds-list__value">상세 현황 보기</span>
                        <span className="sds-list__chev">›</span>
                    </button>
                    {users.length === 0 ? (
                        <p className="sds-list__empty">해당 사용자가 없습니다.</p>
                    ) : shown.map(u => (
                        <button
                            key={u.user_id}
                            className="sds-list__row sds-list__row--clickable"
                            onClick={() => navigate(`/DashboardDetail/${u.user_id}`)}
                        >
                            <i className={`sds-list__icon sds-list__icon--sm sds-list__icon--${tone}`} aria-hidden="true">
                                {(u.userName || '?').charAt(0)}
                            </i>
                            <div className="sds-list__body">
                                <b>{u.userName}</b>
                                <small>{u.groupName}</small>
                            </div>
                            <span className="sds-list__spacer" />
                            <span className={`sds-badge sds-badge--${tone}`}>위험인자 {u.riskCount}개</span>
                            <span className="sds-list__chev">›</span>
                        </button>
                    ))}
                    {/* 접힌 인원이 있을 때만 그린다. 머리줄의 "상세 현황 보기"는 접힘과
                        무관하게 그룹 전원을 대시보드로 넘긴다 — 보이는 만큼만 넘기면
                        더 보기를 누르지 않은 담당자가 일부만 보고 판단하게 된다. */}
                    {hidden > 0 && (
                        <button
                            className="sds-list__row sds-list__row--clickable control-center-group__more"
                            aria-expanded={expanded}
                            onClick={() => setExpandedGroups((prev) => ({ ...prev, [filterLabel]: !prev[filterLabel] }))}
                        >
                            <span className="sds-list__body">
                                {expanded ? '접기' : `${hidden}명 더 보기`}
                            </span>
                            {expanded
                                ? <ChevronUp size={16} aria-hidden="true" />
                                : <ChevronDown size={16} aria-hidden="true" />}
                        </button>
                    )}
                </div>
                );
            };

            return (
                <div className="control-center-groups">
                    {renderIntensiveGroup('위험요인 4개 이상', users4, '위험요인 4개 이상 그룹', 'danger')}
                    {renderIntensiveGroup('위험요인 3개', users3, '위험요인 3개 그룹', 'caution')}
                    {renderIntensiveGroup('위험요인 2개', users2, '위험요인 2개 그룹', 'none')}
                </div>
            );
        }

        let sortedUsers = userIds
            .map((user_id) => {
                const userData = data[user_id] || [];
                let tabData;
                if (type === 'BP_SYSTOLIC') {
                    tabData = userData.find(record => record.measure_type === 'BP_SYSTOLIC');
                } else if (type === 'BP_DIASTOLIC') {
                    tabData = userData.find(record => record.measure_type === 'BP_DIASTOLIC');
                } else {
                    tabData = userData.find(record => record.measure_type === type);
                }
                return {
                    user_id: String(user_id),
                    riskInfo: tabData
                        ? getRiskLevel(tabData.current_value, type)
                        : { level: "기록 없음", color: "#AAAAAA" },
                    userName: tabData ? tabData.name : (userData.length > 0 ? userData[0].name : undefined)
                };
            });

        if (type === 'BLOOD_GLUCOSE') {
            sortedUsers = sortedUsers.sort((a, b) => {
                return BLOOD_GLUCOSE_ORDER.indexOf(a.riskInfo.level) - BLOOD_GLUCOSE_ORDER.indexOf(b.riskInfo.level);
            });
        } else if (type === 'BLOOD_GLUCOSE_MEAL') {
            sortedUsers = sortedUsers.sort((a, b) => {
                return BLOOD_GLUCOSE_MEAL_ORDER.indexOf(a.riskInfo.level) - BLOOD_GLUCOSE_MEAL_ORDER.indexOf(b.riskInfo.level);
            });
        } else if (type === 'BP_SYSTOLIC') {
            sortedUsers = sortedUsers.sort((a, b) => {
                return BP_SYSTOLIC_ORDER.indexOf(a.riskInfo.level) - BP_SYSTOLIC_ORDER.indexOf(b.riskInfo.level);
            });
        } else if (type === 'BP_DIASTOLIC') {
            sortedUsers = sortedUsers.sort((a, b) => {
                return BP_DIASTOLIC_ORDER.indexOf(a.riskInfo.level) - BP_DIASTOLIC_ORDER.indexOf(b.riskInfo.level);
            });
        } else if (type === 'BMI') {
            sortedUsers = sortedUsers.sort((a, b) => {
                return BMI_ORDER.indexOf(a.riskInfo.level) - BMI_ORDER.indexOf(b.riskInfo.level);
            });
        } else {
            sortedUsers = sortedUsers.sort((a, b) => {
                const getGroupPriority = (level) => {
                    if (riskGroups.severe.includes(level)) return 5;
                    if (riskGroups.risk.includes(level)) return 4;
                    if (riskGroups.caution.includes(level)) return 3;
                    if (riskGroups.safe.includes(level)) return 2;
                    return 1;
                };
                return getGroupPriority(b.riskInfo.level) - getGroupPriority(a.riskInfo.level);
            });
        }

        const gridItems = [];
        const riskCounts = {
            severe: 0,
            risk: 0,
            caution: 0,
            safe: 0,
            noRecord: 0
        };

        sortedUsers.forEach(user => {
            if (riskGroups.severe.includes(user.riskInfo.level)) riskCounts.severe++;
            else if (riskGroups.risk.includes(user.riskInfo.level)) riskCounts.risk++;
            else if (riskGroups.caution.includes(user.riskInfo.level)) riskCounts.caution++;
            else if (riskGroups.safe.includes(user.riskInfo.level)) riskCounts.safe++;
            else riskCounts.noRecord++;
        });

        for (let i = 0; i < totalItems; i++) {
            if (i < sortedUsers.length) {
                const { user_id, riskInfo, userName } = sortedUsers[i];
                gridItems.push(
                    <button
                        key={i}
                        onClick={() => riskInfo.level !== "기록 없음" ? handleUserClick(user_id) : navigate(`/DashboardDetail/${user_id}`)}
                        className={`sds-cell sds-cell--clickable sds-cell--${riskStatusKey(riskInfo.level)} control-center-grid-item`}
                        title={userName ? `${userName} · ${riskInfo.level}` : riskInfo.level}
                        aria-label={userName ? `${userName} · ${riskInfo.level}` : riskInfo.level}
                    />
                );
            } else {
                gridItems.push(
                    <div key={i} className="control-center-grid-empty-item" />
                );
            }
        }

        const totalUsers = sortedUsers.length;

        return (
            <div className="sds-cells control-center-cells">
                {gridItems}
            </div>
        );
    };

    const calculateDistribution = () => {
        const getInitialDistribution = () => {
            switch (activeTab) {
                case 'INTENSIVE_CARE':
                    return {};
                case 'BLOOD_GLUCOSE':
                    return {
                        "위험 (고혈당)": 0,
                        "경고 (고혈당 주의)": 0,
                        "정상": 0,
                        "위험 (저혈당)": 0,
                        "기록 없음": 0
                    };
                case 'BLOOD_GLUCOSE_MEAL':
                    return {
                        "위험 (고혈당)": 0,
                        "경고 (고혈당 주의)": 0,
                        "정상": 0,
                        "위험 (저혈당)": 0,
                        "기록 없음": 0
                    };
                case 'BP_SYSTOLIC':
                    return {
                        "위험 (고혈압)": 0,
                        "경고 (고혈압 주의)": 0,
                        "정상": 0,
                        "위험 (저혈압)": 0,
                        "기록 없음": 0
                    };
                case 'BP_DIASTOLIC':
                    return {
                        "위험 (고혈압)": 0,
                        "경고 (고혈압 주의)": 0,
                        "정상": 0,
                        "위험 (저혈압)": 0,
                        "기록 없음": 0
                    };
                case 'BMI':
                    return {
                        "위험 (비만)": 0,
                        "경고 (과체중 주의)": 0,
                        "정상": 0,
                        "위험 (저체중)": 0,
                        "기록 없음": 0
                    };
                default:
                    return {};
            }
        };

        const distribution = getInitialDistribution();

        if (activeTab === 'INTENSIVE_CARE') {
            return distribution;
        }

        Object.keys(data).forEach(user_id => {
            const userData = data[user_id];
            let tabData = null;
            if (userData && userData.length > 0) {
                tabData = userData.find(record => record.measure_type === activeTab);
            }
            if (tabData && typeof tabData.current_value === 'number' && !isNaN(tabData.current_value)) {
                const riskLevel = getRiskLevel(tabData.current_value, activeTab);
                if (distribution.hasOwnProperty(riskLevel.level)) {
                    distribution[riskLevel.level]++;
                } else {
                    distribution["기록 없음"]++;
                }
            } else {
                distribution["기록 없음"]++;
            }
        });

        return distribution;
    };

    const RiskDistributionTable = ({ data }) => {
        if (activeTab === 'INTENSIVE_CARE') return null; // 중점관리 탭에서는 위험도 분포 렌더링 생략

        const getRiskDescriptions = () => {
            switch (activeTab) {
                case 'BLOOD_GLUCOSE':
                    return {
                        "위험 (저혈당)": "측정값 < 70",
                        "정상": "70 ≤ 측정값 ≤ 100",
                        "경고 (고혈당 주의)": "100 < 측정값 ≤ 125",
                        "위험 (고혈당)": "측정값 > 125",
                        "기록 없음": "기록되지 않음"
                    };
                case 'BLOOD_GLUCOSE_MEAL':
                    return {
                        "위험 (저혈당)": "측정값 < 90",
                        "정상": "90 ≤ 측정값 ≤ 140",
                        "경고 (고혈당 주의)": "140 < 측정값 ≤ 200",
                        "위험 (고혈당)": "측정값 > 200",
                        "기록 없음": "기록되지 않음"
                    };
                case 'BP_SYSTOLIC':
                    return {
                        "위험 (저혈압)": "측정값 < 90",
                        "정상": "90 ≤ 측정값 ≤ 120",
                        "경고 (고혈압 주의)": "120 < 측정값 ≤ 140",
                        "위험 (고혈압)": "측정값 > 140",
                        "기록 없음": "기록되지 않음"
                    };
                case 'BP_DIASTOLIC':
                    return {
                        "위험 (저혈압)": "측정값 < 60",
                        "정상": "60 ≤ 측정값 ≤ 80",
                        "경고 (고혈압 주의)": "80 < 측정값 ≤ 90",
                        "위험 (고혈압)": "측정값 > 90",
                        "기록 없음": "기록되지 않음"
                    };
                case 'BMI':
                    return {
                        "위험 (비만)": "측정값 > 25.0",
                        "경고 (과체중 주의)": "23.0 < 측정값 ≤ 25.0",
                        "정상": "18.5 ≤ 측정값 ≤ 23.0",
                        "위험 (저체중)": "측정값 < 18.5",
                        "기록 없음": "기록되지 않음"
                    };
                default:
                    return {};
            }
        };

        const riskDescriptions = getRiskDescriptions();
        const distribution = calculateDistribution();
        const totalUsers = Object.values(distribution).reduce((sum, count) => sum + count, 0);
        const levels = activeTab === 'BLOOD_GLUCOSE'
            ? BLOOD_GLUCOSE_ORDER
            : activeTab === 'BLOOD_GLUCOSE_MEAL'
                ? BLOOD_GLUCOSE_MEAL_ORDER
                : activeTab === 'BP_SYSTOLIC'
                    ? BP_SYSTOLIC_ORDER
                    : activeTab === 'BP_DIASTOLIC'
                        ? BP_DIASTOLIC_ORDER
                        : activeTab === 'BMI'
                            ? BMI_ORDER
                            : Object.keys(distribution);
        return (
            <div className="control-center-risk-distribution-wrapper">
                <div className="sds-stat-grid sds-stat-grid--5">
                    {levels.map((level) => {
                        const count = distribution[level] || 0;
                        const pct = totalUsers > 0 ? (count / totalUsers * 100).toFixed(1) : '0.0';
                        return (
                            <div key={level} className={`sds-stat-card sds-stat-card--${riskStatusKey(level)}`}>
                                <div className="sds-stat-card__value">{count}<span className="sds-stat-card__unit">명</span></div>
                                <div className="sds-stat-card__label">{level}</div>
                                <div className="sds-stat-card__delta">{pct}% · 총 {totalUsers}명</div>
                            </div>
                        );
                    })}
                </div>
                <div className="sds-list control-center-criteria">
                    <div className="sds-list__title">기준치 설명</div>
                    <div className="control-center-criteria__grid">
                        {levels.map((level) => (
                            <div key={level} className="sds-list__row">
                                <i className={`sds-list__icon sds-list__icon--sm sds-list__icon--${riskStatusKey(level)}`} aria-hidden="true">
                                    {riskStatusKey(level) === 'normal' ? '✓' : riskStatusKey(level) === 'danger-low' ? '↓' : riskStatusKey(level) === 'none' ? '–' : '!'}
                                </i>
                                <div className="sds-list__body">
                                    <b>{level}</b>
                                    <small>{riskDescriptions[level]}</small>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    // 서버가 준 최신·직전 쌍을 이 화면의 getRiskLevel 로 등급 매겨 악화만 남긴다.
    //
    // 예전 구현은 data[user_id] 의 [0]·[1] 을 비교했는데, 혈압 탭에서는 필터가
    // 수축기와 이완기를 함께 남기는 탓에 보통 같은 시각의 수축기와 이완기를
    // 서로 견주고 있었다. metric 이 붙은 지금은 그 혼선이 없다.
    const getWorseningUsers = (type) => worsening
        .map((entry) => {
            const previousValue = Number(entry.previous.value);
            const latestValue = Number(entry.latest.value);
            const previousRisk = getRiskLevel(previousValue, type);
            const latestRisk = getRiskLevel(latestValue, type);
            return { entry, previousValue, latestValue, previousRisk, latestRisk };
        })
        .filter(({ previousRisk, latestRisk }) => {
            const before = severityRank(previousRisk.level);
            const after = severityRank(latestRisk.level);
            return before !== null && after !== null && after > before;
        })
        .map(({ entry, previousValue, latestValue, previousRisk, latestRisk }) => ({
            user_id: entry.appUserId,
            name: entry.name,
            birthdate: entry.birthdate,
            gender: entry.gender,
            management_group: entry.groupName,
            previousValue,
            latestValue,
            previousDate: entry.previous.measureDate,
            latestDate: entry.latest.measureDate,
            previousRisk: previousRisk.level,
            latestRisk: latestRisk.level,
            previousColor: previousRisk.color,
            latestColor: latestRisk.color
        }));

    // 위험도가 악화된 사용자 다이얼로그.
    // 기존 모달(control-center-modal)과 같은 구조를 쓴다.
    const WorseningUsersDialog = () => {
        const worseningUsers = getWorseningUsers(activeTab);

        // 행을 누르면 차트 모달이 열린다. 다이얼로그를 겹쳐 두면 두 개가 쌓이므로
        // 일단 닫고, 차트를 닫을 때 closeModal 이 목록을 다시 띄운다.
        const openChart = (userId) => {
            setIsWorseningOpen(false);
            setCameFromWorsening(true);
            handleUserClick(userId);
        };

        return (
            <>
                <div className="control-center-modal">
                    <div className="control-center-modal-content">
                        <div className="control-center-modal-header">
                            <h2 className="control-center-modal-title">위험도가 악화된 사용자</h2>
                            <button
                                className="control-center-modal-close-btn"
                                onClick={() => setIsWorseningOpen(false)}
                            >
                                닫기
                            </button>
                        </div>

                        <table className="control-center-worsening-users-table">
                            <thead>
                                <tr>
                                    <th className="control-center-worsening-users-header">사용자 ID</th>
                                    <th className="control-center-worsening-users-header">이전 기록</th>
                                    <th className="control-center-worsening-users-header">최근 기록</th>
                                    <th className="control-center-worsening-users-header">위험도 변화</th>
                                </tr>
                            </thead>
                            <tbody>
                                {worseningUsers.length > 0 ? (
                                    worseningUsers.map((user) => (
                                        <tr
                                            key={user.user_id}
                                            className="control-center-worsening-users-row"
                                            onClick={() => openChart(user.user_id)}
                                        >
                                            <td className="control-center-worsening-users-cell">{user.user_id}</td>
                                            <td className="control-center-worsening-users-cell">
                                                {typeof user.previousValue === 'number' ? user.previousValue.toFixed(1) : user.previousValue} ({formatDate(user.previousDate)})
                                            </td>
                                            <td className="control-center-worsening-users-cell">
                                                {typeof user.latestValue === 'number' ? user.latestValue.toFixed(1) : user.latestValue} ({formatDate(user.latestDate)})
                                            </td>
                                            <td className="control-center-worsening-users-cell">
                                                <span style={{ color: user.previousColor }}>{user.previousRisk}</span>
                                                <span style={{ margin: '0 8px', color: '#666' }}>→</span>
                                                <span style={{ color: user.latestColor, fontWeight: 'bold' }}>{user.latestRisk}</span>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="4" className="control-center-no-data-message">
                                            데이터가 없습니다.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="control-center-modal-overlay" onClick={() => setIsWorseningOpen(false)} />
            </>
        );
    };


    const history = selectedUser && selectedUser.history ? [...selectedUser.history].sort((a, b) => new Date(a.measure_date) - new Date(b.measure_date)) : [];

    // 월의 1일/말일로 보정하는 함수 추가
    const getMonthStart = dateStr => {
        const d = new Date(dateStr);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
    };
    const getMonthEnd = dateStr => {
        const d = new Date(dateStr);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()}`;
    };
    const minDate = history.length > 0 ? history[0].measure_date : undefined;
    const maxDate = history.length > 0 ? history[history.length - 1].measure_date : undefined;

    const isDark = false;

    return (
        <div className="control-center-container control-center-page">
            <NavigationBar />
            <div className="control-center-content">
                <div className="control-center-risk-distribution-wrapper">
                    <RiskDistributionTable data={data} />
                </div>

                <div className="sds-seg sds-seg--lg control-center-tabs">
                    {[
                        ['INTENSIVE_CARE', '중점관리'],
                        ['BLOOD_GLUCOSE', '식전/공복혈당'],
                        ['BLOOD_GLUCOSE_MEAL', '식후혈당'],
                        ['BP_SYSTOLIC', '혈압'],
                        ['BMI', '체질량지수'],
                    ].map(([key, label]) => (
                        <button
                            key={key}
                            aria-current={activeTab === key ? 'true' : undefined}
                            onClick={() => setActiveTab(key)}
                            className={`sds-seg__item${activeTab === key ? ' is-active' : ''}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {/* 중점관리 탭에는 지표가 특정되지 않아 "직전 대비"가 성립하지 않는다. */}
                {activeTab !== 'INTENSIVE_CARE' && (
                    <div className="sds-alert-row control-center-worsening">
                        <span className="sds-alert-row__icon"><TrendingUp size={20} aria-hidden="true" /></span>
                        <div className="sds-alert-row__body">
                            <b>위험도가 악화된 사용자 {getWorseningUsers(activeTab).length}명</b>
                            <small>직전 측정보다 한 단계 이상 나빠진 대상자입니다.</small>
                        </div>
                        <div className="sds-alert-row__action">
                            <button className="sds-btn sds-btn--weak sds-btn--sm" onClick={() => setIsWorseningOpen(true)}>
                                명단 보기
                            </button>
                        </div>
                    </div>
                )}

                <div className="control-center-grid-container">
                    {isLoading && activeTab === 'INTENSIVE_CARE' ? (
                        <div style={{ width: '100%', padding: '50px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div style={{ width: '50%', marginBottom: '15px', color: '#666', fontWeight: 'bold', textAlign: 'center' }}>
                                중점관리 인원을 불러오는 중입니다...
                            </div>
                            <div className="progress" style={{ width: '50%', height: '20px' }}>
                                <div className="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" style={{ width: '100%' }}></div>
                            </div>
                        </div>
                    ) : (
                        renderGrid(activeTab)
                    )}
                </div>

                {isWorseningOpen && <WorseningUsersDialog />}

                {isModalOpen && selectedUser && (
                    <>
                        <div className="control-center-modal">
                            <div className="control-center-modal-content">
                                <div className="control-center-modal-header">
                                    <h2 className="control-center-modal-title">
                                        {activeTab === 'BLOOD_GLUCOSE' && '혈당 상세보기'}
                                        {activeTab === 'BLOOD_GLUCOSE_MEAL' && '식후혈당 상세보기'}
                                        {activeTab === 'BP_SYSTOLIC' && '혈압 상세보기'}
                                        {activeTab === 'BMI' && '체질량지수 상세보기'}
                                    </h2>
                                    <div className="control-center-modal-actions">
                                        {/* 이 화면에서 바로 보낼 수 있게 둔다. 대상자를 확인하고
                                            다시 대시보드로 찾아 들어가지 않아도 된다. */}
                                        <button
                                            className="control-center-send-btn"
                                            onClick={() => setIsSmsOpen(true)}
                                        >
                                            ✉ 문자 전송
                                        </button>
                                        <button
                                            className="control-center-send-btn"
                                            onClick={() => setIsVideoOpen(true)}
                                        >
                                            ▶ 교육영상 전송
                                        </button>
                                        <button className="control-center-modal-close-btn" onClick={closeModal}>닫기</button>
                                    </div>
                                </div>

                                <div className="control-center-patient-info-grid">
                                    <div className="control-center-patient-info-grid-container">
                                        <div className="control-center-patient-info-label">환자명</div>
                                        <div className="control-center-patient-info-value">{selectedUser.name}</div>

                                        <div className="control-center-patient-info-label">소속기관</div>
                                        <div className="control-center-patient-info-value">{selectedUser.management_group}</div>
                                    </div>

                                    <div className="control-center-patient-info-grid-container">
                                        <div className="control-center-patient-info-label">최종입력 수치</div>
                                        <div className="control-center-patient-info-value">{activeTab === 'BMI' && typeof selectedUser.latestValue === 'number' ? selectedUser.latestValue.toFixed(1) : selectedUser.latestValue}</div>

                                        <div className="control-center-patient-info-label">현재 위험도</div>
                                        <div className="control-center-patient-info-value">{selectedUser.latestRisk}</div>

                                        <div className="control-center-patient-info-label">최종입력 일자</div>
                                        <div className="control-center-patient-info-value">{dateFormatter(selectedUser.history[selectedUser.history.length - 1].measure_date)}</div>
                                    </div>
                                </div>

                                <div className="control-center-chart-section">
                                    <h2 className="control-center-chart-title">측정 기록</h2>
                                    <div className="control-center-chart-container">
                                        <Line
                                            key={selectedUser?.userId + '-' + activeTab}
                                            data={{
                                                labels: [...selectedUser.history]
                                                    .sort((a, b) => new Date(a.measure_date) - new Date(b.measure_date))
                                                    .map(record => {
                                                        const date = new Date(record.measure_date);
                                                        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                                                    }),
                                                datasets: activeTab === 'BP_SYSTOLIC' ? [
                                                    {
                                                        label: '수축기',
                                                        data: [...selectedUser.history]
                                                            .sort((a, b) => new Date(a.measure_date) - new Date(b.measure_date))
                                                            .filter(record => record.measure_type === 'BP_SYSTOLIC')
                                                            .map(record => ({
                                                                x: new Date(record.measure_date).toISOString().split('T')[0],
                                                                y: record.current_value
                                                            })),
                                                        borderColor: 'rgb(75, 192, 192)',
                                                        backgroundColor: 'transparent',
                                                        fill: false,
                                                        tension: 0.4,
                                                        borderWidth: 2,
                                                        pointRadius: 0,
                                                        pointHoverRadius: 0,
                                                        measure_type: 'BP_SYSTOLIC'
                                                    },
                                                    {
                                                        label: '이완기',
                                                        data: [...selectedUser.history]
                                                            .sort((a, b) => new Date(a.measure_date) - new Date(b.measure_date))
                                                            .filter(record => record.measure_type === 'BP_DIASTOLIC')
                                                            .map(record => ({
                                                                x: new Date(record.measure_date).toISOString().split('T')[0],
                                                                y: record.current_value
                                                            })),
                                                        borderColor: 'rgb(255, 99, 132)',
                                                        backgroundColor: 'transparent',
                                                        fill: false,
                                                        tension: 0.4,
                                                        borderWidth: 2,
                                                        pointRadius: 0,
                                                        pointHoverRadius: 0,
                                                        measure_type: 'BP_DIASTOLIC'
                                                    }
                                                ] : [
                                                    {
                                                        label:
                                                            activeTab === 'BLOOD_GLUCOSE' ? '혈당'
                                                                : activeTab === 'BLOOD_GLUCOSE_MEAL' ? '식후혈당'
                                                                    : activeTab === 'BMI' ? '체질량지수'
                                                                        : '측정값',
                                                        data: [...selectedUser.history]
                                                            .sort((a, b) => new Date(a.measure_date) - new Date(b.measure_date))
                                                            .map(record => ({
                                                                x: new Date(record.measure_date).toISOString().split('T')[0],
                                                                y: activeTab === 'BMI' && typeof record.current_value === 'number' ? record.current_value.toFixed(1) : record.current_value
                                                            })),
                                                        borderColor: 'rgb(75, 192, 192)',
                                                        backgroundColor: 'transparent',
                                                        fill: false,
                                                        tension: 0.4,
                                                        borderWidth: 2,
                                                        pointRadius: 0,
                                                        pointHoverRadius: 0,
                                                        measure_type: activeTab
                                                    }
                                                ]
                                            }}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                scales: {
                                                    y: {
                                                        beginAtZero: false,
                                                        suggestedMin: activeTab === 'BLOOD_GLUCOSE' ? 40 :
                                                            activeTab === 'BMI' ? 15 :
                                                                activeTab === 'BP_SYSTOLIC' ? 60 : 40,
                                                        suggestedMax: activeTab === 'BLOOD_GLUCOSE' ? 200 :
                                                            activeTab === 'BMI' ? 35 :
                                                                activeTab === 'BP_SYSTOLIC' ? 180 : 160,
                                                        title: {
                                                            display: true,
                                                            text: activeTab === 'BLOOD_GLUCOSE' ? '혈당 수치' :
                                                                activeTab === 'BMI' ? 'BMI 수치' :
                                                                    '혈압 수치',
                                                            color: isDark ? '#fff' : '#222',
                                                            font: { size: 16, weight: 'bold' }
                                                        },
                                                        ticks: {
                                                            color: isDark ? '#fff' : '#222',
                                                            font: { size: 14 },
                                                        }
                                                    },
                                                    x: {
                                                        type: 'time',
                                                        adapters: {
                                                            date: {
                                                                locale: ko
                                                            }
                                                        },
                                                        time: {
                                                            unit: 'day',
                                                            displayFormats: {
                                                                day: 'MM-dd'
                                                            }
                                                        },
                                                        min: minDate,
                                                        title: {
                                                            display: true,
                                                            text: '날짜',
                                                            color: isDark ? '#fff' : '#222',
                                                            font: { size: 16, weight: 'bold' }
                                                        },
                                                        ticks: {
                                                            color: isDark ? '#fff' : '#222',
                                                            font: { size: 14 },
                                                            source: 'auto',
                                                            maxRotation: 90,
                                                            minRotation: 90,
                                                            callback: function (value, index, ticks) {
                                                                const labelDate = new Date(value);
                                                                if (isNaN(labelDate)) return '';
                                                                return `${String(labelDate.getMonth() + 1).padStart(2, '0')}-${String(labelDate.getDate()).padStart(2, '0')}`;
                                                            },
                                                            align: 'center'
                                                        }
                                                    }
                                                },
                                                plugins: {
                                                    legend: {
                                                        display: true,
                                                        position: 'top',
                                                        labels: {
                                                            color: isDark ? '#fff' : '#222',
                                                            font: { size: 14 }
                                                        }
                                                    },
                                                    tooltip: {
                                                        mode: 'index',
                                                        intersect: false,
                                                        callbacks: {
                                                            label: function (context) {
                                                                let label = context.dataset.label || '';
                                                                if (label) {
                                                                    label += ': ';
                                                                }
                                                                label += context.parsed.y;
                                                                return label;
                                                            }
                                                        }
                                                    },
                                                    safeRangesPlugin: true
                                                },
                                                interaction: {
                                                    mode: 'nearest',
                                                    axis: 'x',
                                                    intersect: false
                                                },
                                                safeRanges: activeTab === 'BP_SYSTOLIC' ? [...(Array.isArray(systolicRange) ? systolicRange : []), ...(Array.isArray(diastolicRange) ? diastolicRange : [])] : activeTab === 'BP_DIASTOLIC' ? [...(Array.isArray(systolicRange) ? systolicRange : []), ...(Array.isArray(diastolicRange) ? diastolicRange : [])] : activeTab === 'BLOOD_GLUCOSE' ? [
                                                    { min: 70, max: 100, color: 'rgba(46,204,64,0.2)', lineColor: 'rgba(46,204,64,0.8)' },
                                                    { min: 100, max: 125, color: 'rgba(191,255,0,0.2)', lineColor: 'rgba(191,255,0,0.8)' }
                                                ] : activeTab === 'BLOOD_GLUCOSE_MEAL' ? [
                                                    { min: 90, max: 140, color: 'rgba(46,204,64,0.2)', lineColor: 'rgba(46,204,64,0.8)' },
                                                    { min: 140, max: 200, color: 'rgba(255,255,0,0.2)', lineColor: 'rgba(255,255,0,0.8)' }
                                                ] : activeTab === 'BMI' ? [
                                                    { min: 18.5, max: 23, color: 'rgba(46,204,64,0.2)', lineColor: 'rgba(46,204,64,0.8)' },
                                                    { min: 23, max: 25, color: 'rgba(191,255,0,0.2)', lineColor: 'rgba(191,255,0,0.8)' }
                                                ] : []
                                            }}
                                        />
                                    </div>
                                </div>

                                <div className="control-center-history-table-section">
                                    <table className="control-center-history-table-container">
                                        <thead>
                                            <tr>
                                                <th className="control-center-history-table-header">날짜</th>
                                                <th className="control-center-history-table-header">
                                                    {activeTab === 'BP_SYSTOLIC' ? '수축기/이완기 혈압' : '측정값'}
                                                </th>
                                                <th className="control-center-history-table-header">위험도</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[...new Set(selectedUser.history.map(record => record.measure_date))]
                                                .map((date, index) => {
                                                    const recordsForDate = selectedUser.history.filter(r => r.measure_date === date);
                                                    const systolicRecord = recordsForDate.find(r => r.measure_type === 'BP_SYSTOLIC');
                                                    const diastolicRecord = recordsForDate.find(r => r.measure_type === 'BP_DIASTOLIC');
                                                    const otherRecord = recordsForDate.find(r => !['BP_SYSTOLIC', 'BP_DIASTOLIC'].includes(r.measure_type));

                                                    const value = activeTab === 'BP_SYSTOLIC' ?
                                                        `${systolicRecord?.current_value || '-'}/${diastolicRecord?.current_value || '-'}` :
                                                        (activeTab === 'BMI' && typeof otherRecord?.current_value === 'number' ? otherRecord.current_value.toFixed(1) : otherRecord?.current_value);

                                                    const riskValue = activeTab === 'BP_SYSTOLIC' ?
                                                        systolicRecord?.current_value :
                                                        otherRecord?.current_value;

                                                    return (
                                                        <tr key={index}>
                                                            <td className="control-center-history-table-cell">{dateFormatter(date)}</td>
                                                            <td className="control-center-history-table-cell">{value}</td>
                                                            <td className="control-center-history-table-cell">
                                                                {riskValue ? getRiskLevel(riskValue, activeTab).level : '-'}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                        <div className="control-center-modal-overlay" onClick={closeModal} />

                        {/* ⚠️ EducationalVideoDialog 는 user.user_id / name / mobile 을 쓴다.
                            로스터에는 mobile 이 없어서(성능 때문에 뺐다) 다이얼로그가
                            직접 받아 오도록 두 컴포넌트 모두 appUserId 만 넘긴다. */}
                        <SendSmsDialog
                            isOpen={isSmsOpen}
                            onClose={() => setIsSmsOpen(false)}
                            appUserId={selectedUser.userId}
                            name={selectedUser.name}
                            groupName={selectedUser.management_group}
                        />
                        <EducationalVideoDialog
                            isOpen={isVideoOpen}
                            onClose={() => setIsVideoOpen(false)}
                            user={{
                                user_id: selectedUser.userId,
                                name: selectedUser.name,
                                mobile: selectedUser.mobile
                            }}
                        />
                    </>
                )}
            </div>
        </div>
    );
}
